import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  CheckboxButton,
  Dropdown,
  Spinner,
  Subtitle,
  Title,
} from '../../components/shared';
import { showFeedback } from '../../store/ui/ui.actions';
import axios from 'axios';
import { selectFarmsData } from '../../store/farms/farms.selector';

const uploadExcel = async (
  file: any,
  farmId: string | null,
  createNew: boolean,
) => {
  try {
    const Authorization = {
      Authorization: `Bearer ${localStorage.getItem('marine-farm')}`,
    };
    const formData = new FormData();
    formData.append('excel', file);
    if (farmId) {
      formData.append('farm_id', farmId);
    }
    if (createNew) formData.append('create_new', 'true');

    return axios.post(
      `${process.env.REACT_APP_API_URL}api/import/line-location`,
      formData,
      {
        headers: {
          ...Authorization,
          Accept: '*',
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  } catch (error) {
    throw error;
  }
};

interface IResult {
  total_count: number;
  failed_count: number;
  failed_data: Array<{ line_uid: string; line_name: string; comment: string }>;
}

const ImportLinesPage = () => {
  const dispatch = useDispatch<any>();
  const farmsData = useSelector(selectFarmsData);
  const [farmId, setFarmId] = useState('0');
  const [createNew, setCreateNew] = useState(false);

  const [disabled, setDisabled] = useState(false);
  const [file, setFile] = useState<any>(null);
  const [result, setResult] = useState<IResult>();

  const onFileChange = (e: any) => {
    if (e.target.files && e.target.files.length > 0) setFile(e.target.files[0]);
  };
  const submitFile = () => {
    setResult(undefined);
    if (Number(farmId) === 0) {
      window.alert('Please select a farm');
    } else if (!file) {
      window.alert('No file selected');
    } else {
      setDisabled(true);
      uploadExcel(file, Number(farmId) <= 0 ? null : farmId, createNew)
        .then(r => {
          setDisabled(false);
          setResult(r.data);
          if ((r.data?.failed_count ?? 0) <= 0) {
            dispatch(
              showFeedback({
                type: 'success',
                isMessage: true,
                message: 'Imported successfully',
              }),
            );
          }
        })
        .catch(err => {
          setDisabled(false);
          alert(err?.message ?? 'Failed to upload');
        });
    }
  };

  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative'>
        <div className='report-page upload-page'>
          <div className='report--header d-flex justify-between align-center'>
            <Title size={5} color='black-3' align='default' fontWeight={700}>
              Upload Line Excel file
            </Title>
          </div>
          <div className='report--body'>
            <div className='farm-line'>
              <div className='d-flex mt-17 mb-17 justify-between align-end'>
                <div className='d-flex justify-between align-end'>
                  <div className='mr-17'>
                    <CheckboxButton
                      label='Create new'
                      checked={createNew}
                      onChange={e => setCreateNew(e.target.checked)}
                      className='mb-17'
                    />
                    <Dropdown
                      options={[
                        { id: '0', value: '0', label: 'Select a farm' },
                        ...farmsData.map(x => ({
                          id: x.id.toString(),
                          value: x.id.toString(),
                          label: `${x.name} - ${x.farm_number}`,
                        })),
                      ]}
                      value={farmId}
                      onChange={v => setFarmId(v)}
                      label='Select a farm'
                    />
                  </div>
                  <div className='mr-17'>
                    <input
                      type='file'
                      className='input'
                      accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                      onChange={onFileChange}
                    />
                  </div>
                  <div>
                    <Button
                      width={'wide'}
                      size={2}
                      type='fill'
                      color='green'
                      className='ml-16'
                      onClick={submitFile}
                      disabled={disabled}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
                <div className='ml-17 result-summary'>
                  <div className='info-box'>
                    Total: {result?.total_count ?? '-'}
                  </div>
                  <div className='info-box'>
                    Success:
                    {result ? result.total_count - result.failed_count : ' -'}
                  </div>
                  <div className='info-box'>
                    Failed: {result?.failed_count ?? '-'}
                  </div>
                </div>
              </div>
              {disabled ? (
                <div className='mt-32'>
                  <Spinner />
                </div>
              ) : (
                <div className='mt-17 result-body'>
                  <div style={{ flex: 5 }}>
                    <Subtitle
                      className='mb-17'
                      size={5}
                      color='black-3'
                      align='default'
                      fontWeight={700}
                    >
                      Failed data
                    </Subtitle>
                    {result &&
                      result.failed_data.map((item, idx) => (
                        <div key={idx} className='data-container'>
                          <div className='data-info'>
                            <div className='--info-item'>
                              <div className='--label'>Line UID</div>
                              <div className='--value'>{item.line_uid}</div>
                            </div>
                            <div className='--info-item'>
                              <div className='--label'>Line Name</div>
                              <div className='--value'>{item.line_name}</div>
                            </div>
                          </div>
                          <div className='message-info'>
                            <div className='--label'>Message</div>
                            <div className='--message'>{item.comment}</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportLinesPage;
